import { request, url, language, util } from '@tencent/ppd-base-libs';
import { NcovDataType } from '../types';
import { autoEnDate } from '@/util';
import { i18n } from '@/i18n';
let initKeyMap = false;
const gKeyMap: any = {};
let gKeyMapSource: { pageKey: string; sourceType: string; dataKey: string }[] = [];

function getLang() {
  let lang = language.getLanguage();
  lang = lang.replace(/-/g, '_');
  if (lang !== 'en' && lang !== 'zh_hk') {
    return 'zh_hk';
  }
  return lang;
}

export function queryInfo(param) {
  return request.get(`/cgi-bin/refill/ncov_data_get.fcgi?noreport=1&data_type=${param.data_type}&language=en`);
}

export async function queryTraffic() {
  const lang = getLang();
  await genKeyMap();
  return request
    .get(`/cgi-bin/refill/ncov_data_get.fcgi?noreport=1&data_type=${NcovDataType.flightsTrainsList}&language=${lang}`)
    .then((data) => {
      const { ncov_data } = data;
      const keyMaps = gKeyMap.flightsTrainsList || {};
      const f_ncov_data = ncov_data.map((item) => ({
        date: item[keyMaps.date] || item['Date of travel'],
        tranId: item[keyMaps.tranId] || item['Flight/Train/Ship number'],
        point: item[keyMaps.point] || item['Departure & arrival'],
        static: item[keyMaps.static] || item['Related probable/confirmed cases'],
      }));
      data.f_ncov_data = f_ncov_data.reverse();
      return data;
    });
}

export async function queryCases(lang = '') {
  lang = lang || getLang();
  await genKeyMap();
  return request
    .get(`/cgi-bin/refill/ncov_data_get.fcgi?noreport=1&data_type=${NcovDataType.geAn}&language=${lang}`)
    .then((data) => {
      const { ncov_data } = data;
      const keyMaps = gKeyMap.geAn || {};
      const f_ncov_data = ncov_data.map((item) => ({
        index: item[keyMaps.index] || item['Case no.'],
        date: item[keyMaps.date] || item['Report date'],
        dateOnset: item[keyMaps.dateOnset] || item['Date of onset'],
        sex: item[keyMaps.sex] || item.Gender,
        age: item[keyMaps.age] || item.Age,
        status: item[keyMaps.status] || item['Hospitalised/Discharged/Deceased'],
        type: item[keyMaps.type] || item['HK/Non-HK resident'],
        hospital: item[keyMaps.hospital] || item['Name of hospital admitted'],
        caseClassification: item[keyMaps.caseClassification] || item['Case classification'],
        confirmedOrProbable: item[keyMaps.confirmedOrProbable] || item['Confirmed/probable'],
      }));
      data.f_ncov_data = f_ncov_data.reverse();
      return data;
    });
}

export async function queryCaseNumber() {
  const lang = getLang();
  await genKeyMap();
  return request
    .get(`/cgi-bin/refill/ncov_data_get.fcgi?noreport=1&data_type=${NcovDataType.cases}&language=${lang}`)
    .then((data) => {
      const { ncov_data } = data;
      const keyMaps = gKeyMap.cases || {};
      const f_ncov_data = ncov_data.map((item) => ({
        date: item[keyMaps.date] || item['As of date'],
        time: item[keyMaps.time] || item['As of time'],
        updateTime: item['Update timestamp'],
        fulfilling: item[keyMaps.fulfilling] || item['Number of cases fulfilling the reporting criteria'],
        fulfilling_diff:
          item[keyMaps.fulfilling_diff] || item['Number of cases fulfilling the reporting criteria diff'],
        hospitalised: item[keyMaps.hospitalised] || item['Number of cases still hospitalised for investigation'],
        hospitalised_diff:
          item[keyMaps.hospitalised_diff] || item['Number of cases still hospitalised for investigation diff'],
        confirmed: item[keyMaps.confirmed] || item['Number of confirmed cases'],
        confirmed_diff: item[keyMaps.confirmed_diff] || item['Number of confirmed cases diff'],
        rat_positive_delta: item.RAT_Positive_Delta || '',
        rat_positive: item.RAT_Positive || '',
        death: item[keyMaps.death] || item['Number of death cases'],
        death_diff: item[keyMaps.death_diff] || item['Number of death cases diff'],
        discharge: item[keyMaps.discharge] || item['Number of discharge cases'],
        discharge_diff: item[keyMaps.discharge_diff] || item['Number of discharge cases diff'],
        ruledOut: item[keyMaps.ruledOut] || item['Number of ruled out cases'],
        ruledOut_diff: item[keyMaps.ruledOut_diff] || item['Number of ruled out cases diff'],
        probable: item[keyMaps.probable] || item['Number of probable cases'],
        probable_diff: item[keyMaps.probable_diff] || item['Number of probable cases diff'],
        condition: item[keyMaps.condition] || item['Number of hospitalised cases in critical condition'],
        condition_diff: item[keyMaps.condition_diff] || item['Number of hospitalised cases in critical condition diff'],
      }));
      data.f_ncov_data = f_ncov_data;
      data.serverTime = data['system time'];
      return data;
    });
}

export function queryBuildingList() {
  const lang = getLang();
  return request
    .get(`/cgi-bin/refill/ncov_data_get.fcgi?noreport=1&data_type=${NcovDataType.buildingList}&language=${lang}`)
    .then((data) => {
      const { ncov_data } = data;
      const keyMaps = gKeyMap.buildingList || {};
      const f_ncov_data = ncov_data.map((item) => {
        let district = item[keyMaps.district] || item.District;
        if (lang === 'zh_hk' && district && district[district.length - 1] !== '區') {
          district = `${district}區`;
        }
        const bN = item[keyMaps.buildingName] || item['Building name'];
        const buildingName = bN.replace('(非住宅)', '').replace('(non-residential)', '');
        return {
          buildingName,
          date: item[keyMaps.date] || item['Last date of residence of the case(s)'],
          district,
          cases: item[keyMaps.cases] || item['Related probable/confirmed cases'],
        };
      });
      data.f_ncov_data = f_ncov_data;
      return data;
    });
}

export function queryHomeConfinees() {
  const lang = getLang();
  return request
    .get(
      `/cgi-bin/refill/ncov_data_get.fcgi?noreport=1&data_type=${NcovDataType.buildingListHomeConfinees}&language=${lang}`,
    )
    .then((data) => {
      const { ncov_data } = data;
      const keyMaps = gKeyMap.building || {};
      const f_ncov_data = ncov_data.map((item) => ({
        buildingName: item[keyMaps.buildingName] || item['Name of Building'],
        date: item[keyMaps.date] || item['End Date of Home Quarantine'],
        district: item[keyMaps.district] || item.District,
        cases: item[keyMaps.cases] || item['No.'],
      }));
      data.f_ncov_data = f_ncov_data;
      return data;
    });
}

export function getNewFromCgi() {
  let lang = 'zh_hk';
  if (getLang() === 'en') {
    lang = 'en';
  }
  return request
    .get(`/cgi-bin/charity/ncov_news.fcgi?noreport=1&language=${lang}`)
    .then((data) =>
      data.ncov_news.map((item) =>
        lang === 'zh_hk'
          ? {
              content: item.content,
              recommend: '',
              source: '香港政府新聞網',
              time: item.time,
              title: item.title,
            }
          : {
              content: item.content,
              recommend: '',
              source: 'news.gov.hk',
              time: item.time,
              title: item.title,
            },
      ),
    )
    .catch((err) => []);
}

export function queryNews() {
  const lang = getLang();
  return request.loadVtools('/vtools/hkDisease_utf8.json', 1000 * 60 * 5).then((data) => {
    if (data.hkDisease && Array.isArray(data.hkDisease)) {
      data.hkDisease = data.hkDisease.reverse();
    }
    // 新版source改造, vtools配置时可选择source配置
    data.hkDisease = data.hkDisease.map((item) => {
      item.source = item.source_sel || item.source;
      return item;
    });
    if (lang === 'en') {
      data.hkDisease = data.hkDisease.map((item) => ({
        title: item.title_en || item.title,
        time: item.time_en || autoEnDate(item.time), // 如果有填英文时间则展示英文时间，否则自动转换为英文
        content: item.content_en || item.content,
        source: item.source_en || i18n(item.source), // 如果有填英文来源则展示英文来源
        recommend: item.recommend || '0', // 是否上架滚动轮播
      }));
    }
    if (data.service && Array.isArray(data.service)) {
      data.service = data.service.map((item) => {
        const name = lang === 'en' ? item.en_name : item.cn_name;
        return {
          name,
          bgimage: lang == 'en' ? item.en_bgimage : item.cn_bgimage,
          icon: item.icon,
          color: item.color,
          jump_link: item.jump_link,
        };
      });
    }
    if (data.banner && Array.isArray(data.banner)) {
      data.banner = data.banner.find((item) => item.online == '1');
    }
    if (data.recommend && Array.isArray(data.recommend)) {
      data.recommend = data.recommend.map((item) => ({
        title: lang == 'en' ? item.title_en : item.title_cn,
        jump_link: lang == 'en' ? item.jump_link_en || item.jump_link : item.jump_link,
      }));
    }

    return getNewFromCgi().then((newData) => {
      console.log('newsData', newData);
      data.hkDisease = newData;
      return data;
    });
  });
}

export async function genKeyMap() {
  if (!initKeyMap) {
    const [err, data] = await util.to(queryNews());
    if (err) {
      // 这要是跪了就惨了，不过上面的接口会有默认的 key 读取做兜底
      return;
    }
    gKeyMapSource = data.keyMap;
    gKeyMapSource.forEach((keyMapItem) => {
      if (!gKeyMap[keyMapItem.sourceType]) {
        gKeyMap[keyMapItem.sourceType] = {};
      }
      gKeyMap[keyMapItem.sourceType][keyMapItem.dataKey] = keyMapItem.pageKey;
    });
    initKeyMap = true;
  }
}
